import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Truck, 
  Heart, 
  Clock, 
  Gift, 
  ChefHat, 
  Leaf, 
  Star,
  ArrowRight,
  Check,
  Sparkles,
  MessageCircle,
  MapPin,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8dcb2a80-f509-441b-b99e-0f9224a5cf02/images/f0506046716245de1c91b95a5a468ef5fdc3b05e9b2c7b9bdb4e110afa482103.png";
const KITCHEN_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8dcb2a80-f509-441b-b99e-0f9224a5cf02/images/dcfa7c44541789e660e20bb6104287fff73602b7f39c56f0a427c827327feb10.png";
const PUZZLE_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8dcb2a80-f509-441b-b99e-0f9224a5cf02/images/a4084aee315b52af6649aa3859baee0e1e6f21bfc1812aaf5367ee77fc68e8f2.png";
const AIRPODS_IMAGE = "https://images.unsplash.com/photo-1606741965429-8d76ff50bb2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxhcHBsZSUyMGFpcnBvZHMlMjBwcm98ZW58MHx8fHwxNzc0NzA3MTMxfDA&ixlib=rb-4.1.0&q=85&w=400";

export default function Home() {
  const navigate = useNavigate();
  const [sampleName, setSampleName] = useState('');
  const [sampleEmail, setSampleEmail] = useState('');
  const [samplePhone, setSamplePhone] = useState('');
  const [samplePref, setSamplePref] = useState('delivery');
  const [sampleAddress, setSampleAddress] = useState('');
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleClaimed, setSampleClaimed] = useState(false);
  const [sampleStatus, setSampleStatus] = useState(null);

  const fetchSampleStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/free-sample/status`);
      setSampleStatus(res.data);
    } catch (e) {
      // Status fetch failed
    }
  }, []);

  useEffect(() => {
    fetchSampleStatus();
  }, [fetchSampleStatus]);

  const handleClaimSample = async (e) => {
    e.preventDefault();
    if (!sampleName || !sampleEmail || !samplePhone) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (samplePref === 'delivery' && !sampleAddress) {
      toast.error('Please enter your delivery address');
      return;
    }
    setSampleLoading(true);
    try {
      const res = await axios.post(`${API}/free-sample/claim`, {
        name: sampleName,
        email: sampleEmail,
        phone: samplePhone,
        preference: samplePref,
        address: sampleAddress || null
      });
      setSampleClaimed(true);
      toast.success(res.data.message);
      fetchSampleStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim sample');
    } finally {
      setSampleLoading(false);
    }
  };

  const features = [
    {
      id: "delivery",
      icon: <Truck className="w-6 h-6" />,
      title: "Free Delivery",
      description: "Fresh meals delivered right to your doorstep in Bristol, every single day"
    },
    {
      id: "healthy",
      icon: <Heart className="w-6 h-6" />,
      title: "Healthy & Nutritious",
      description: "Balanced meals with vegetables, proteins, and fruits daily"
    },
    {
      id: "menu",
      icon: <Clock className="w-6 h-6" />,
      title: "7-Day Menu",
      description: "Variety throughout the week with our carefully planned menu"
    },
    {
      id: "referral",
      icon: <Gift className="w-6 h-6" />,
      title: "Referral Rewards",
      description: "Refer friends and earn discounts, up to free food for a month! Refer 25 friends to unlock it."
    }
  ];

  const includedItems = [];

  return (
    <div className="min-h-screen bg-[#FAF6F0]" data-testid="home-page">
      {/* Launch Announcement Banner */}
      <div className="bg-[#2C402E] text-white py-3 px-4" data-testid="launch-banner">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="font-semibold text-sm sm:text-base">Subscriptions start from 15th June!</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] sm:min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-2xl">
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6" data-testid="hero-title">
              Healthy Food,<br />
              <span className="text-[#D4AF37]">Healthy You</span>
            </h1>
            <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8 leading-relaxed font-light pr-12 sm:pr-0">
              Fresh, healthy meals delivered daily in Bristol. Our 7-day rotating menu brings you the comfort of great cooking with the convenience of delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pr-12 sm:pr-0">
              <Button 
                size="lg" 
                className="bg-[#E05D3A] hover:bg-[#C94F2E] text-white font-semibold text-sm sm:text-lg px-6 sm:px-8 py-5 sm:py-6 rounded-full shadow-xl"
                onClick={() => navigate('/subscribe')}
                data-testid="hero-subscribe-btn"
              >
                <span className="hidden sm:inline">Weekly from £60 | Monthly from £229 | Single Order from £15</span>
                <span className="sm:hidden">Subscribe from £15</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/20 font-semibold text-sm sm:text-lg px-6 sm:px-8 py-5 sm:py-6 rounded-full bg-transparent"
                onClick={() => navigate('/menu')}
                data-testid="hero-menu-btn"
              >
                View Menu
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FREE SAMPLE WEEK Section */}
      <section className="py-12 md:py-20 bg-[#2C402E]" data-testid="free-sample-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Badge className="bg-[#D4AF37] text-[#2C402E] mb-4 rounded-full px-4 py-1 text-sm font-bold">FREE SAMPLE WEEK</Badge>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-3">
              Try Our Food For Free!
            </h2>
            <p className="text-[#FAF6F0]/80 text-lg max-w-2xl mx-auto">
              13th & 14th June | 20 free meals per day | One meal per person | Book now!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* What you get */}
            <div className="bg-white/10 rounded-2xl p-6 sm:p-8">
              <h3 className="font-serif text-xl font-bold text-[#D4AF37] mb-4">Your Free Complete Meal</h3>
              <ul className="space-y-3 mb-6">
                {['Lamb Biryani', '3 Chapathis with Curry', 'Boiled Egg', 'Fruit Salad', 'Vegetable Salad'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white">
                    <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="space-y-3">
                <div className="flex items-start gap-3 text-white/80 text-sm">
                  <Truck className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Delivery</strong> — We deliver to your door in Bristol</span>
                </div>
                <div className="flex items-start gap-3 text-white/80 text-sm">
                  <MapPin className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Collection</strong> — 12-14 Gainsborough Square, Lockleaze, BS7 9XA</span>
                </div>
              </div>
              {sampleStatus && (
                <div className="mt-6 bg-white/10 rounded-xl p-4 text-center">
                  {sampleStatus.status === 'filled' ? (
                    <p className="text-[#D4AF37] font-bold">All free sample slots are filled! Subscribe to enjoy our meals.</p>
                  ) : sampleStatus.status === 'booking' ? (
                    <p className="text-[#D4AF37] font-semibold">Bookings open now! Free meals on 13th & 14th June.</p>
                  ) : sampleStatus.past_cutoff ? (
                    <p className="text-[#D4AF37] font-semibold">Today's claims closed at 5 PM. Come back tomorrow!</p>
                  ) : sampleStatus.sold_out ? (
                    <p className="text-[#E05D3A] font-bold">Sold out for today! Try again tomorrow.</p>
                  ) : sampleStatus.status === 'upcoming' ? (
                    <p className="text-[#D4AF37] font-semibold">Starts {sampleStatus.starts}!</p>
                  ) : sampleStatus.status === 'ended' ? (
                    <p className="text-white/60">Free sample days have ended. Subscribe to enjoy our meals!</p>
                  ) : (
                    <p className="text-[#D4AF37] font-semibold">{sampleStatus.remaining_today} of 20 meals remaining today (claims close at 5 PM)</p>
                  )}
                </div>
              )}
            </div>

            {/* Claim Form */}
            <Card className="bg-white rounded-2xl">
              <div className="p-6 sm:p-8">
                {sampleClaimed ? (
                  <div className="text-center py-8">
                    <Check className="w-16 h-16 text-[#2C402E] mx-auto mb-4" />
                    <h3 className="font-serif text-2xl font-bold text-[#2C402E] mb-2">Claimed!</h3>
                    <p className="text-[#4A4A4A] mb-4">Your free meal has been reserved. We'll be in touch soon!</p>
                    <div className="bg-[#D4AF37]/10 rounded-xl p-4 mb-4">
                      <p className="text-[#D4AF37] font-bold">You've unlocked £10 off your 1st month subscription!</p>
                      <p className="text-sm text-[#4A4A4A] mt-1">Subscribe now to claim your discount.</p>
                    </div>
                    <Button onClick={() => navigate('/subscribe')} className="bg-[#E05D3A] hover:bg-[#C94F2E]">
                      Subscribe Now - £10 Off <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleClaimSample} className="space-y-4">
                    <h3 className="font-serif text-xl font-bold text-[#2C402E] mb-2">Claim Your Free Meal</h3>
                    <div>
                      <Label htmlFor="sample-name">Full Name *</Label>
                      <Input id="sample-name" placeholder="Your name" value={sampleName} onChange={(e) => setSampleName(e.target.value)} required data-testid="sample-name" />
                    </div>
                    <div>
                      <Label htmlFor="sample-email">Email Address *</Label>
                      <Input id="sample-email" type="email" placeholder="your@email.com" value={sampleEmail} onChange={(e) => setSampleEmail(e.target.value)} required data-testid="sample-email" />
                    </div>
                    <div>
                      <Label htmlFor="sample-phone">Mobile Number *</Label>
                      <Input id="sample-phone" type="tel" placeholder="07xxxxxxxxx" value={samplePhone} onChange={(e) => setSamplePhone(e.target.value)} required data-testid="sample-phone" />
                    </div>
                    <div>
                      <Label>Preference</Label>
                      <RadioGroup value={samplePref} onValueChange={setSamplePref} className="flex gap-4 mt-1">
                        <Label htmlFor="pref-delivery" className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${samplePref === 'delivery' ? 'border-[#E05D3A] bg-[#E05D3A]/5' : 'border-gray-200'}`}>
                          <RadioGroupItem value="delivery" id="pref-delivery" />
                          <Truck className="w-4 h-4" /> Delivery
                        </Label>
                        <Label htmlFor="pref-collection" className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${samplePref === 'collection' ? 'border-[#2C402E] bg-[#2C402E]/5' : 'border-gray-200'}`}>
                          <RadioGroupItem value="collection" id="pref-collection" />
                          <MapPin className="w-4 h-4" /> Collection
                        </Label>
                      </RadioGroup>
                    </div>
                    {samplePref === 'delivery' && (
                      <div>
                        <Label htmlFor="sample-address">Delivery Address *</Label>
                        <Textarea id="sample-address" placeholder="Your full address in Bristol" value={sampleAddress} onChange={(e) => setSampleAddress(e.target.value)} rows={2} data-testid="sample-address" />
                      </div>
                    )}
                    {samplePref === 'collection' && (
                      <div className="bg-[#FAF6F0] p-3 rounded-lg text-sm text-[#2C402E]">
                        <MapPin className="w-4 h-4 inline mr-1 text-[#E05D3A]" />
                        <strong>Collection Point:</strong> 12-14 Gainsborough Square, Lockleaze, BS7 9XA
                      </div>
                    )}
                    <Button
                      type="submit"
                      className="w-full bg-[#E05D3A] hover:bg-[#C94F2E] py-5 text-lg font-semibold rounded-xl"
                      disabled={sampleLoading || sampleStatus?.sold_out || sampleStatus?.past_cutoff || sampleStatus?.status === 'filled'}
                      data-testid="sample-claim-btn"
                    >
                      {sampleLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Claiming...</>
                      ) : sampleStatus?.status === 'filled' ? (
                        'All Slots Filled'
                      ) : sampleStatus?.past_cutoff ? (
                        'Claims Closed (After 5 PM)'
                      ) : sampleStatus?.sold_out ? (
                        'Sold Out Today'
                      ) : (
                        'Claim My Free Meal'
                      )}
                    </Button>
                    <p className="text-xs text-[#4A4A4A] text-center">One free meal per person. Limited to 20 per day.</p>
                    <p className="text-xs text-[#D4AF37] text-center font-semibold bg-[#D4AF37]/10 rounded-lg py-2 px-3">Claim your sample &amp; subscribe to get £10 off your 1st month!</p>
                  </form>
                )}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-[#FAF6F0]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#E05D3A] mb-4">
              Why Choose Us
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C402E]">
              The ANAN's Kitchen Difference
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <Card 
                key={feature.id}
                className="p-8 bg-white border border-[#2C402E]/10 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`feature-card-${feature.id}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#E05D3A]/10 flex items-center justify-center text-[#E05D3A] mb-6">
                  {feature.icon}
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#2C402E] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="included-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#E05D3A] mb-4">
                Every Meal Includes
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C402E] mb-6">
                Complete Nutrition in Every Box
              </h2>
              <p className="text-[#4A4A4A] text-lg mb-8 leading-relaxed">
                Every day meal includes a portion of vegetable salad and fruit salad, a boiled egg, 3 chapathis with a curry or a fry, along with the main dish.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 bg-[#FAF6F0] rounded-2xl p-5">
                  <div className="w-10 h-10 rounded-full bg-[#E05D3A] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C402E] text-lg">Boiled Egg, Vegetable Salad & Fruit Salad</p>
                    <p className="text-sm text-[#4A4A4A]">A healthy, fresh start packed with nutrients</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-[#FAF6F0] rounded-2xl p-5">
                  <div className="w-10 h-10 rounded-full bg-[#E05D3A] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C402E] text-lg">3 Chapathis served with Curry or a Fry</p>
                    <p className="text-sm text-[#4A4A4A]">Freshly made chapathis with your choice of side</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-[#FAF6F0] rounded-2xl p-5">
                  <div className="w-10 h-10 rounded-full bg-[#E05D3A] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C402E] text-lg">Biryani or Rice with a Curry</p>
                    <p className="text-sm text-[#4A4A4A]">A hearty, flavourful main course to complete your day</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6 p-6 bg-[#FAF6F0] rounded-2xl">
                <div>
                  <div className="text-3xl font-bold text-[#E05D3A]">£60<span className="text-base font-normal text-[#4A4A4A]">/week (veg)</span></div>
                  <div className="text-3xl font-bold text-[#E05D3A]">£229<span className="text-base font-normal text-[#4A4A4A]">/month (veg)</span></div>
                </div>
                <div>
                  <p className="font-semibold text-[#2C402E]">Flexible Plans</p>
                  <p className="text-sm text-[#4A4A4A]">Veg & Non-Veg options</p>
                </div>
              </div>

              {/* Workplace Delivery */}
              <div className="mt-6 p-5 bg-[#2C402E] rounded-2xl text-white" data-testid="workplace-delivery">
                <p className="font-serif font-semibold text-lg mb-2">Workplace Delivery in Bristol</p>
                <p className="text-[#FAF6F0]/80 text-sm mb-3">
                  We also deliver to workplaces in Bristol in the same delivery slots — 12-3 PM and 5-10 PM.
                </p>
                <div className="flex gap-4">
                  <div className="bg-white/10 rounded-lg px-4 py-2 text-center">
                    <p className="text-[#D4AF37] font-bold text-lg">+£20</p>
                    <p className="text-xs text-[#FAF6F0]/60">weekly</p>
                  </div>
                  <div className="bg-white/10 rounded-lg px-4 py-2 text-center">
                    <p className="text-[#D4AF37] font-bold text-lg">+£50</p>
                    <p className="text-xs text-[#FAF6F0]/60">monthly</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={KITCHEN_IMAGE}
                alt="Fresh Kitchen"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-2xl shadow-xl">
                <div className="flex items-center gap-3">
                  <Leaf className="w-6 h-6 text-[#2C402E]" />
                  <span className="font-serif font-semibold text-[#2C402E] text-lg">Fresh & Healthy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Puzzle Section */}
      <section className="py-20 md:py-32 bg-[#2C402E] text-white" data-testid="puzzle-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-[#D4AF37] text-[#2C402E] mb-6 rounded-full px-4 py-1">
                Monthly Contest
              </Badge>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-6">
                Win Apple AirPods!
              </h2>
              <p className="text-[#FAF6F0]/80 text-lg mb-8 leading-relaxed">
                Every 15th of the month at <span className="font-bold text-[#D4AF37]">15:15:15 UK time</span>, our puzzle unlocks. Be the first to solve it and win amazing prizes!
              </p>
              
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-8">
                <h3 className="font-serif font-semibold text-xl mb-6">How It Works</h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#E05D3A] flex items-center justify-center font-bold">1</div>
                    <span>Be a subscriber</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#E05D3A] flex items-center justify-center font-bold">2</div>
                    <span>Visit puzzle page on 15th at 15:15:15 UK time</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#E05D3A] flex items-center justify-center font-bold">3</div>
                    <span>First correct answer wins!</span>
                  </li>
                </ul>
              </div>

              <Button 
                size="lg"
                className="bg-[#D4AF37] text-[#2C402E] hover:bg-[#c4a030] font-bold rounded-full px-8"
                onClick={() => navigate('/puzzle')}
                data-testid="puzzle-cta-btn"
              >
                Go to Puzzle Page
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            <div className="relative flex justify-center">
              <img 
                src={PUZZLE_IMAGE}
                alt="Spices Puzzle"
                className="w-80 h-80 object-cover rounded-3xl shadow-2xl"
              />
              <img 
                src={AIRPODS_IMAGE}
                alt="Apple AirPods"
                className="absolute bottom-0 right-0 w-32 h-32 object-cover rounded-2xl shadow-2xl animate-float"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Referral Section */}
      <section className="py-20 md:py-32 bg-[#FAF6F0]" data-testid="referral-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#E05D3A] to-[#C94F2E] rounded-3xl p-10 md:p-16 text-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-6">
                  Refer & Earn Rewards
                </h2>
                <p className="text-white/80 text-lg mb-8 leading-relaxed">
                  Share the love of healthy eating with friends and family. Everyone wins!
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4 bg-white/10 rounded-2xl p-5">
                    <Gift className="w-8 h-8 text-[#D4AF37]" />
                    <div>
                      <p className="font-semibold text-lg">Refer 1 Friend</p>
                      <p className="text-white/70 text-sm">Get £10 off your subscription</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 bg-white/10 rounded-2xl p-5">
                    <Star className="w-8 h-8 text-[#D4AF37]" />
                    <div>
                      <p className="font-semibold text-lg">Refer 25 Friends</p>
                      <p className="text-white/70 text-sm">FREE food for entire month!</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="inline-block bg-white/20 rounded-3xl p-10">
                  <ChefHat className="w-20 h-20 mx-auto text-[#D4AF37] mb-6" />
                  <p className="font-serif text-2xl font-semibold mb-6">Ready to Start?</p>
                  <Button 
                    size="lg"
                    className="bg-white text-[#E05D3A] hover:bg-[#FAF6F0] font-bold rounded-full px-8"
                    onClick={() => navigate('/subscribe')}
                    data-testid="referral-subscribe-btn"
                  >
                    Subscribe Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C402E] mb-6">
            Ready for Healthy, Delicious Meals?
          </h2>
          <p className="text-[#4A4A4A] text-lg mb-10 leading-relaxed">
            Join hundreds of happy customers in Bristol who enjoy our freshly prepared meals every day. Subscribe now and taste the difference!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg"
              className="bg-[#E05D3A] hover:bg-[#C94F2E] font-bold text-lg px-10 rounded-full"
              onClick={() => navigate('/subscribe')}
              data-testid="cta-subscribe-btn"
            >
              View All Plans
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-[#2C402E] text-[#2C402E] hover:bg-[#2C402E] hover:text-white font-bold text-lg px-10 rounded-full"
              onClick={() => navigate('/menu')}
              data-testid="cta-menu-btn"
            >
              Explore Menu
            </Button>
          </div>
          <div className="mt-6">
            <a
              href="https://wa.me/4407827893614?text=Hi%20ANAN's%20Kitchen!%20I'd%20like%20to%20know%20more%20about%20your%20meal%20plans."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-semibold text-lg px-8 py-3 rounded-full shadow-lg transition-all hover:scale-105"
              data-testid="cta-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
